"use client";

import { useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronDownIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import { AdminProductsAttentionNavBadge } from "@/components/nav/AdminProductsAttentionNavBadge";
import { SellerMyItemsAttentionNavIndicator } from "@/components/nav/SellerMyItemsAttentionNavIndicator";
import {
  SIGN_OUT_LABEL,
  buildAccountMenuItems,
  formatHeaderAccountLabel,
} from "@/lib/layout/site-nav";

type DesktopAccountMenuProps = {
  name: string | null | undefined;
  avatarUrl: string | null | undefined;
  isAdmin: boolean;
  showInviteFriends: boolean;
  myItemsNeedsAttention: boolean;
  myItemsAttentionCount: number;
  adminProductsAttentionCount: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLogout: () => void;
};

export default function DesktopAccountMenu({
  name,
  avatarUrl,
  isAdmin,
  showInviteFriends,
  myItemsNeedsAttention,
  myItemsAttentionCount,
  adminProductsAttentionCount,
  open,
  onOpenChange,
  onLogout,
}: DesktopAccountMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const items = buildAccountMenuItems({ isAdmin, showInviteFriends });
  const accountLabel = formatHeaderAccountLabel(name);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, onOpenChange]);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        className="flex items-center rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => onOpenChange(!open)}
      >
        <span className="sr-only">Open account menu</span>
        <span className="mr-2 flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-secondary">
          {avatarUrl ? (
            <Image
              src={avatarUrl}
              alt=""
              width={32}
              height={32}
              className="h-full w-full object-cover"
              unoptimized={process.env.NODE_ENV === "development"}
            />
          ) : (
            <UserCircleIcon
              className="h-8 w-8 text-text-light"
              aria-hidden="true"
            />
          )}
        </span>
        <span className="mr-1 max-w-[9rem] truncate text-sm font-medium text-foreground">
          {accountLabel}
        </span>
        <ChevronDownIcon
          className="h-5 w-5 text-text-light"
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-10 mt-2 w-64 origin-top-right overflow-hidden rounded-md bg-surface py-1 shadow-lg ring-1 ring-black ring-opacity-5"
        >
          {items.map((item) => {
            const ariaLabel =
              item.id === "admin" && adminProductsAttentionCount > 0
                ? `Admin Dashboard, ${adminProductsAttentionCount} products need review`
                : item.id === "my-stuff" && myItemsNeedsAttention
                  ? `My stuff, ${myItemsAttentionCount} need your attention`
                  : item.label;
            return (
              <Link
                key={item.id}
                href={item.href}
                role="menuitem"
                className="flex items-center justify-between gap-2 px-4 py-2 text-sm text-foreground hover:bg-tertiary"
                aria-label={ariaLabel}
                onClick={() => onOpenChange(false)}
              >
                <span>{item.label}</span>
                {item.id === "admin" ? (
                  <AdminProductsAttentionNavBadge
                    count={adminProductsAttentionCount}
                  />
                ) : null}
                {item.id === "my-stuff" ? (
                  <SellerMyItemsAttentionNavIndicator
                    show={myItemsNeedsAttention}
                    count={myItemsAttentionCount}
                  />
                ) : null}
              </Link>
            );
          })}
          <button
            type="button"
            role="menuitem"
            className="block w-full border-t border-border px-4 py-2 text-left text-sm text-foreground hover:bg-tertiary"
            onClick={onLogout}
          >
            {SIGN_OUT_LABEL}
          </button>
        </div>
      ) : null}
    </div>
  );
}
